import { useRef, useState } from "react";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { FaroMenu, FaroMenuItem, ThreeDotsIcon } from "@faro-lotv/flat-ui";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { RevertUnpublishedChangesMenuItem } from "@pages/data-management/data-management-menu/revert-unpublished-changes-menu-item";
import { assert } from "@faro-lotv/foundation";

interface Props extends BaseProjectIdProps {
  /** Callback to set the flag whether the publish panel is visible. */
  setIsPublishPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /** Workflow state of the project */
  state: WorkflowState;
  shouldShowRepublish: boolean;
  shouldShowRevert: boolean;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
  /** Boolean to detect if dropzone loader is active */
  isLoading? : boolean;
  /** Callback to set the dropzone spinner while fetching data from ProjectApi */
  setIsLoading? : React.Dispatch<React.SetStateAction<boolean>>;
  /** Callback to show/hide the spinner in dropzone/table view when revert btn is clicked */
  setIsRevertBtnClicked? : React.Dispatch<React.SetStateAction<boolean>>;
}

/** The three dots button and associated menu in the Staging Area workflow bar. */
export function DataManagementDotsButton({
  setIsPublishPanelOpen,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- projectId is prepared for the upcoming components (see below)
  projectId,
  state,
  shouldShowRepublish,
  shouldShowRevert,
  setShouldPreventRedirect,
  isLoading,
  setIsLoading,
  setIsRevertBtnClicked,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const menuRef = useRef<HTMLButtonElement>(null);

  assert(shouldShowRepublish || shouldShowRevert, "At least one menu entry should be visible!");

  function republish(): void {
    trackEvent({ name: DataManagementEvents.republish });
    setIsMenuOpen(false);
    setIsPublishPanelOpen(true);
  }

  return (
    <>
      <SphereTooltip title="Additional Actions">
        <FaroIconButton
          dataTestId="sa-dots-button"
          aria-label="more"
          aria-haspopup="true"
          component={ThreeDotsIcon}
          onClick={() => setIsMenuOpen(true)}
          iconButtonProps={{ ref: menuRef }}
        />
      </SphereTooltip>

      <FaroMenu
        anchorEl={menuRef.current}
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        transformOrigin={{
          vertical: -10,
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: 36,
        }}
        minWidth="0px"
      >
        { shouldShowRepublish && (
          <SphereTooltip title="Set options and re-publish the project.">
            <FaroMenuItem
              key="Re-publish"
              label="Re-publish"
              onClick={republish}
            />
          </SphereTooltip>
        )}

        { shouldShowRevert && (
          <RevertUnpublishedChangesMenuItem
            state={state}
            setShouldPreventRedirect={setShouldPreventRedirect}
            onClickCallback={() => setIsMenuOpen(false)}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setIsRevertBtnClicked={setIsRevertBtnClicked}
          />
        )}

        {/* Horizontal Divider */}
        {/* <div style={{ height: "1px", backgroundColor: sphereColors.gray200, margin: "8px 0" }} /> */}

        {/* <DeleteProjectMenuItem
          projectId={projectId}
          state={state}
          onClickCallback={handleClose}
        /> */}
      </FaroMenu>
    </>
  );
}
